import { Injectable } from '@angular/core';
import { CardHistoryEntity } from 'src/app/models/entities/cardhistory.entity';
import { Timestamp } from '@angular/fire/firestore';
import { CardHistoryService } from './cardhistory.service';
import { CardDetailsComponent } from 'src/app/components/card-details/card-details.component';
import { Form } from '@angular/forms';
import { CdkFormGroup } from '@onepointfour-npm/cdk';
import { ScoringService } from '../card.score.service';

@Injectable({
    providedIn: 'root',
})
export class HistoryService {
    private recentHistoryCache: Map<string, number> = new Map();
    private readonly DUPLICATE_THRESHOLD_MS = 1000; // Prevent duplicate entries within 1 second

    constructor(private cardHistoryService: CardHistoryService) { }


    public saveHistory(
        boardForm: CdkFormGroup,
        oldValues: Map<string, any>,
        currentUserKey: string,
        editorContent: string,
        lookUpValues: any
    ): Promise<number> {
        
        return new Promise((resolve) => {
            const resolveValue = (value: any, lookup: { list: any[], key: string, display: string }): string => {
                const found = lookup.list.find(item => item[lookup.key] === value);
                return found ? found[lookup.display] : value;
            };


            var user = resolveValue(currentUserKey, lookUpValues['assignedTo']);

            Object.keys(boardForm.controls).forEach((controlName) => {
                const oldValue = oldValues.get(controlName);
                const newValue = boardForm.get(controlName)?.value;

                if (oldValue !== newValue) {
                    const action = 'change';
                    const description = `Field '${controlName}' updated`;

                    let resolvedOldValue = oldValue;
                    let resolvedNewValue = newValue;

                    if (lookUpValues[controlName]) {
                        resolvedOldValue = resolveValue(oldValue, lookUpValues[controlName]);
                        resolvedNewValue = resolveValue(newValue, lookUpValues[controlName]);
                    }

                    if (controlName === 'description') {
                        this.saveCardHistory(action, description, '', '', boardForm, user, controlName);
                    } else {
                        this.saveCardHistory(action, description, resolvedOldValue, resolvedNewValue, boardForm, user, controlName);
                    }
                }
            });

            if (oldValues.get('editor') !== editorContent) {
                const action = 'change';

                this.saveCardHistory('change', 'editor content changed', '', '', boardForm, user, 'description');
            }


        });
    }


    saveCardHistory(action: string, description: string, oldValue: string, newValue: string, boardForm: any, currentUserName: string, controlName: string): void {
        if (boardForm.value.key) {
            // Create a unique cache key for this specific change
            const cacheKey = `${boardForm.value.key}-${controlName}-${oldValue}-${newValue}`;
            const now = Date.now();

            // Check if this exact change was recently saved
            const lastSaved = this.recentHistoryCache.get(cacheKey);
            if (lastSaved && (now - lastSaved) < this.DUPLICATE_THRESHOLD_MS) {
                console.log('[History] Skipping duplicate history entry:', {
                    cardKey: boardForm.value.key,
                    field: controlName,
                    timeSinceLastSave: now - lastSaved
                });
                return;
            }

            var log: CardHistoryEntity = new CardHistoryEntity();
            log.action = action;
            log.cardKey = boardForm.value.key;
            log.description = description;
            log.newValue = newValue;
            log.oldValue = oldValue;
            log.user = currentUserName;
            log.createdAt = new Date();
            log.updatedAt = new Date();
            log.changedDate = now;
            log.fieldName = controlName;
            log.assignedToUserKey = boardForm.value.assignedTo;
            log.assignedToRole = boardForm.value.assignedToRole;

            // Update cache
            this.recentHistoryCache.set(cacheKey, now);

            // Clean up old cache entries
            if (this.recentHistoryCache.size > 100) {
                const entriesToDelete: string[] = [];
                this.recentHistoryCache.forEach((timestamp, key) => {
                    if (now - timestamp > 10000) { // Remove entries older than 10 seconds
                        entriesToDelete.push(key);
                    }
                });
                entriesToDelete.forEach(key => this.recentHistoryCache.delete(key));
            }

            this.cardHistoryService.add(boardForm.value.key, JSON.parse(JSON.stringify(log)))
                .catch(error => {
                    console.error('[History] Failed to save card history:', error);
                    // Don't throw to prevent UI disruption
                });
        }
    }


}
